// Live "how long has this been running" readout for a recording stuck in a non-terminal
// status (uploaded/queued/transcribing) — web-ui-issues #16. AWS Transcribe batch jobs expose
// no percent-complete signal, so the honest affordance is a ticking elapsed timer next to a
// status label + spinner, NOT a fabricated progress bar. Deliberately its own small hook
// (rather than inline `setInterval` in each screen) so RecordingsList's per-card chip and
// RecordingDetail's banner tick from the same logic and stop ticking (but keep their last
// reading, rather than blanking) the moment the recording leaves the in-progress set.
import { useEffect, useState } from 'react';
import type { Recording } from '@scribe/core';

const TICK_MS = 1_000;

/** mm:ss (or h:mm:ss past an hour) elapsed-time label — deliberately coarser than
 * `formatMs`'s "h:mm:ss always" shape isn't needed here since these are short pipeline waits,
 * but shares the same rendering so a recording stuck for over an hour still reads sensibly. */
export function formatElapsed(totalSec: number): string {
  const sec = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  if (m > 0) return `${m}:${String(s).padStart(2, '0')}`;
  return `${s}s`;
}

/**
 * Returns a live-ticking "elapsed since `sinceIso`" label (e.g. `"12s"`, `"2:05"`), updating
 * once a second while `active` is true. Once `active` flips to false (the recording reached a
 * terminal status), the timer stops but the last computed label is kept rather than clearing
 * — so a card that just finished doesn't flash blank before its status treatment swaps to the
 * completed chip. Returns `null` when `sinceIso` is missing/unparseable.
 */
export function useElapsedLabel(sinceIso: string | null | undefined, active: boolean): string | null {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => clearInterval(id);
  }, [active]);

  if (!sinceIso) return null;
  const startMs = new Date(sinceIso).getTime();
  if (Number.isNaN(startMs)) return null;
  return formatElapsed((Date.now() - startMs) / 1000);
}

/** How long a `capture_heartbeat_at` may be stale before we stop calling the capture live.
 * The bridge heartbeats every ~120s and swallows its own failures by design, so one or two
 * missed beats must not flip a recording out of "Recording…" mid-meeting — but the window has
 * to stay well under `reconcile_ingests`'s 1h grace period, or this label would claim a
 * capture is live long after the backend has given up on it. 5 min tolerates two misses. */
const CAPTURE_LIVENESS_MS = 5 * 60 * 1000;

/** The three genuinely distinct things a non-terminal recording can be doing. `status` alone
 * cannot tell them apart: under ADR-004 unified ingest, `transcribing` covers an active
 * capture, the post-stop finalize, AND a running batch job (§0's conflation warning). */
export type InProgressPhase = 'queued' | 'capturing' | 'finalizing' | 'transcribing';

type PhaseFields = Pick<
  Recording,
  'status' | 'transcription_queued_at' | 'transcription_started_at' | 'capture_heartbeat_at'
>;

/**
 * Which phase a non-terminal recording is actually in (§2.12).
 *
 * `transcription_started_at` is the discriminator the backend now guarantees: it is set only
 * when a transcription really started, never at capture bind. So within `transcribing`:
 *   - a fresh `capture_heartbeat_at` -> `capturing` (audio is arriving right now)
 *   - no heartbeat, no start        -> `finalizing` (capture ended; ffmpeg assembly + upload,
 *                                      which scales with recording length — ~24s for 43 min)
 *   - a start stamp                 -> `transcribing` (a real job, honestly timeable)
 */
export function inProgressPhase(recording: PhaseFields, nowMs: number = Date.now()): InProgressPhase {
  if (recording.status === 'queued') return 'queued';
  if (recording.transcription_started_at) return 'transcribing';
  const beat = recording.capture_heartbeat_at ? new Date(recording.capture_heartbeat_at).getTime() : NaN;
  if (!Number.isNaN(beat) && nowMs - beat < CAPTURE_LIVENESS_MS) return 'capturing';
  return 'finalizing';
}

/**
 * The honest anchor for an in-progress elapsed readout, or `null` when there isn't one.
 *
 * `transcription_started_at` for a running transcription, `transcription_queued_at` for the
 * queued-but-not-started window. **Null for `capturing` and `finalizing`** — and that is the
 * §2.12 fix, not an omission. The old chain fell back to `created_at`, which meant a recording
 * that had just stopped showed an elapsed of roughly its own length ("Transcribing… 43:00" and
 * climbing) before snapping to 0 once the real job re-stamped it. There is no honest anchor for
 * those two phases: nothing records when capture ended. Better no number than a wrong one — the
 * phase label carries the meaning instead.
 */
export function inProgressSince(recording: PhaseFields, nowMs: number = Date.now()): string | null {
  switch (inProgressPhase(recording, nowMs)) {
    case 'transcribing':
      return recording.transcription_started_at ?? null;
    case 'queued':
      return recording.transcription_queued_at ?? null;
    default:
      return null;
  }
}
